import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const USER_CACHE_KEY = 'snd_vms_user';

// Decode JWT payload (no signature verification — just for local expiry check)
const getTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // convert to ms
  } catch {
    return null;
  }
};

const isTokenExpired = (token) => {
  const exp = getTokenExpiry(token);
  return exp === null || Date.now() >= exp;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('snd_vms_token'));
  const [loading, setLoading] = useState(true);

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    // Offline or first render: restore from cache if token is still valid
    if (!navigator.onLine) {
      if (!isTokenExpired(token)) {
        const cached = localStorage.getItem(USER_CACHE_KEY);
        if (cached) {
          setUser(JSON.parse(cached));
          setLoading(false);
          return;
        }
      }
      // Token expired offline — can't verify, clear session
      localStorage.removeItem('snd_vms_token');
      localStorage.removeItem(USER_CACHE_KEY);
      setToken(null);
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const res = await api.get('/auth/me');
      setUser(res.data.user);
      // Cache user data for offline use
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(res.data.user));
    } catch (err) {
      // Network error (no response) — restore from cache rather than logging out
      if (!err.response && !isTokenExpired(token)) {
        const cached = localStorage.getItem(USER_CACHE_KEY);
        if (cached) {
          setUser(JSON.parse(cached));
          setLoading(false);
          return;
        }
      }
      // Auth error (401/403) or expired token — clear session
      localStorage.removeItem('snd_vms_token');
      localStorage.removeItem(USER_CACHE_KEY);
      setToken(null);
      setUser(null);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('snd_vms_token', newToken);
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('snd_vms_token');
    localStorage.removeItem(USER_CACHE_KEY);
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'manager',
    isExecutive: user?.role === 'executive',
    api
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
